import { useContext, useState } from "react";
import { Link } from "react-router-dom";
import {
    Breadcrumb,
    Button,
    Col,
    Descriptions,
    Flex,
    Row,
    Space,
    Tag,
    Typography,
} from "antd";
import {
    CheckCircleOutlined,
    ClockCircleOutlined,
    CommentOutlined,
    CustomerServiceOutlined,
    EditOutlined,
    GlobalOutlined,
    MailOutlined,
    MobileOutlined,
    UserOutlined,
} from "@ant-design/icons";

import { ThemeContext } from "../../../../contexts/ThemeContext";
import { ISupport } from "../../interfaces";
import { formatDateStringToDateAndTime } from "../../../../utils/Conversores";
import PhoneSupport from "../PhoneSupport";
import PageHeader from "../../../../components/PageHeader";
import ModalUpdateSupport from "../ModalUpdateSupport";

const { Text, Paragraph } = Typography;

interface IProps {
    support?: ISupport;
    onHandleStatusChange: (status: string, observation?: string) => Promise<void>;
}

export default function SupportForm({ support, onHandleStatusChange }: IProps) {
    const { theme } = useContext(ThemeContext);
    const isDark = theme === "dark";

    const [isUpdateModalVisible, setIsUpdateModalVisible] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const isSite = support?.origin === "Site";
    const backTab = isSite ? "externo-site" : "externo-app";
    const isOpen = support?.status === "Aberto" || support?.status === "OPEN";
    const isAnswered = support?.status === "Respondido";

    const handleSaveStatusAndObservation = async (newStatus: string, newObservation: string) => {
        try {
            setIsSaving(true);
            await onHandleStatusChange(newStatus, newObservation);
            setIsUpdateModalVisible(false);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div
                style={{
                    background: isDark ? "#141414" : "#ffffff",
                    border: `1px solid ${isDark ? "#262626" : "#f0f0f0"}`,
                    padding: "10px 16px",
                    borderRadius: 8,
                }}
            >
                <Breadcrumb
                    items={[
                        {
                            title: <Link to={`/ajuda-e-suporte?tab=${backTab}`}>Ajuda e Suporte</Link>,
                        },
                        {
                            title: `Mensagem #${support?.id ?? "--"}`,
                        },
                    ]}
                />
            </div>
            <PageHeader
                icon={<CustomerServiceOutlined />}
                iconBg="linear-gradient(135deg, #fa8c16 0%, #fa541c 100%)"
                title={`Mensagem de Suporte #${support?.id ?? "--"} (${support?.origin || "Externo"})`}
                tag={
                    isOpen ? (
                        <Tag color="warning" icon={<ClockCircleOutlined />}>
                            Aberto
                        </Tag>
                    ) : isAnswered ? (
                        <Tag color="blue" icon={<CheckCircleOutlined />}>
                            Respondido
                        </Tag>
                    ) : (
                        <Tag color="success" icon={<CheckCircleOutlined />}>
                            Fechado
                        </Tag>
                    )
                }
                subtitle={`Solicitante: ${support?.name || "Visitante"} | Enviado em: ${support?.created_at ? formatDateStringToDateAndTime(support.created_at) : "--"}`}
                actions={
                    <Flex align="center" gap={10} wrap="wrap">
                        <Button
                            type="primary"
                            icon={<EditOutlined />}
                            onClick={() => setIsUpdateModalVisible(true)}
                            style={{ borderRadius: 6, fontWeight: 500 }}
                        >
                            Atualizar Atendimento
                        </Button>
                    </Flex>
                }
            />
            <Row gutter={[16, 16]}>
                <Col xs={24} lg={12}>
                    <div
                        style={{
                            background: isDark ? "#1a1a1a" : "#ffffff",
                            border: `1px solid ${isDark ? "#2a2a2a" : "#e2e8f0"}`,
                            boxShadow: isDark ? "0 1px 3px rgba(0,0,0,0.3)" : "0 1px 3px rgba(0,0,0,0.04)",
                            borderRadius: 10,
                            padding: "18px 20px",
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                        }}
                    >
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                                marginBottom: 16,
                                paddingBottom: 10,
                                borderBottom: `1px solid ${isDark ? "#2a2a2a" : "#eaedf1"}`,
                            }}
                        >
                            <UserOutlined style={{ color: "#fa8c16", fontSize: 16 }} />
                            <div>
                                <Text strong style={{ fontSize: 14, color: isDark ? "#f0f0f0" : "#1e293b", display: "block" }}>
                                    Dados do Solicitante
                                </Text>
                                <Text type="secondary" style={{ fontSize: 11.5 }}>
                                    Informações de contato e canal de envio
                                </Text>
                            </div>
                        </div>

                        <Descriptions
                            column={1}
                            size="small"
                            bordered
                            style={{
                                marginBottom: 16,
                                background: isDark ? "#141414" : "#f8fafc",
                            }}
                        >
                            <Descriptions.Item label="Nome Completo">
                                <span style={{ fontWeight: 600 }}>{support?.name || "--"}</span>
                            </Descriptions.Item>
                            <Descriptions.Item label="Origem">
                                {isSite ? (
                                    <Tag color="blue" icon={<GlobalOutlined />}>
                                        Portal / Site
                                    </Tag>
                                ) : (
                                    <Tag color="orange" icon={<MobileOutlined />}>
                                        Aplicativo
                                    </Tag>
                                )}
                            </Descriptions.Item>
                            <Descriptions.Item label="Telefone">
                                {support?.phone ? (
                                    <PhoneSupport phone={support.phone} />
                                ) : (
                                    <span>--</span>
                                )}
                            </Descriptions.Item>
                            {support?.email && (
                                <Descriptions.Item label="E-mail">
                                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                        <MailOutlined style={{ color: "#1890ff" }} />
                                        <a href={`mailto:${support.email}`}>{support.email}</a>
                                    </div>
                                </Descriptions.Item>
                            )}
                            <Descriptions.Item label="Data de Envio">
                                <span>
                                    {support?.created_at
                                        ? formatDateStringToDateAndTime(support.created_at)
                                        : "--"}
                                </span>
                            </Descriptions.Item>
                            <Descriptions.Item label="Status">
                                {isOpen ? (
                                    <Tag color="warning" icon={<ClockCircleOutlined />}>
                                        Aberto
                                    </Tag>
                                ) : isAnswered ? (
                                    <Tag color="blue" icon={<CheckCircleOutlined />}>
                                        Respondido
                                    </Tag>
                                ) : (
                                    <Tag color="success" icon={<CheckCircleOutlined />}>
                                        Fechado
                                    </Tag>
                                )}
                            </Descriptions.Item>
                            <Descriptions.Item label="Atendente">
                                {support?.panel_user?.name ? (
                                    <span style={{ fontWeight: 600, color: isDark ? "#93c5fd" : "#1d4ed8" }}>
                                        {support.panel_user.name}
                                    </span>
                                ) : (
                                    <span style={{ color: isDark ? "#8c8c8c" : "#94a3b8" }}>Não atribuído</span>
                                )}
                            </Descriptions.Item>
                        </Descriptions>

                        {/* WhatsApp responder card */}
                        {support?.phone && (
                            <div
                                style={{
                                    background: isDark ? "#0f2e1a" : "#f6ffed",
                                    border: `1px solid ${isDark ? "#1e5c33" : "#b7eb8f"}`,
                                    borderRadius: 8,
                                    padding: "12px 16px",
                                    marginTop: "auto",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    flexWrap: "wrap",
                                    gap: 10,
                                }}
                            >
                                <div>
                                    <Text strong style={{ color: isDark ? "#86d99b" : "#389e0d", fontSize: 13, display: "block" }}>
                                        Responder pelo WhatsApp
                                    </Text>
                                    <Text type="secondary" style={{ fontSize: 11.5 }}>
                                        Inicie uma conversa direta com o solicitante no WhatsApp Web/App
                                    </Text>
                                </div>

                                <PhoneSupport phone={support.phone} />
                            </div>
                        )}
                    </div>
                </Col>

                <Col xs={24} lg={12}>
                    <div
                        style={{
                            background: isDark ? "#1a1a1a" : "#ffffff",
                            border: `1px solid ${isDark ? "#2a2a2a" : "#e2e8f0"}`,
                            boxShadow: isDark ? "0 1px 3px rgba(0,0,0,0.3)" : "0 1px 3px rgba(0,0,0,0.04)",
                            borderRadius: 10,
                            padding: "18px 20px",
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                            gap: 14,
                        }}
                    >
                        <div>
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 8,
                                    marginBottom: 16,
                                    paddingBottom: 10,
                                    borderBottom: `1px solid ${isDark ? "#2a2a2a" : "#eaedf1"}`,
                                }}
                            >
                                <CustomerServiceOutlined style={{ color: "#1890ff", fontSize: 16 }} />
                                <div>
                                    <Text strong style={{ fontSize: 14, color: isDark ? "#f0f0f0" : "#1e293b", display: "block" }}>
                                        Mensagem Enviada
                                    </Text>
                                    <Text type="secondary" style={{ fontSize: 11.5 }}>
                                        Conteúdo da solicitação de suporte ou dúvida
                                    </Text>
                                </div>
                            </div>

                            <div
                                style={{
                                    background: isDark ? "#141414" : "#f8fafc",
                                    border: `1px solid ${isDark ? "#262626" : "#e2e8f0"}`,
                                    borderRadius: 8,
                                    padding: "16px 18px",
                                }}
                            >
                                <Paragraph
                                    style={{
                                        margin: 0,
                                        color: isDark ? "#e2e8f0" : "#1e293b",
                                        fontSize: 13.5,
                                        lineHeight: 1.6,
                                        whiteSpace: "pre-wrap",
                                    }}
                                >
                                    {support?.message || "--"}
                                </Paragraph>
                            </div>
                        </div>

                        {/* Internal Observation Card */}
                        <div
                            style={{
                                background: isDark ? "#141414" : "#fafafa",
                                border: `1px solid ${isDark ? "#262626" : "#e2e8f0"}`,
                                borderRadius: 8,
                                padding: "14px 16px",
                                marginTop: "auto",
                            }}
                        >
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    marginBottom: 8,
                                }}
                            >
                                <Space align="center" size={6}>
                                    <CommentOutlined style={{ color: "#1677ff", fontSize: 14 }} />
                                    <Text strong style={{ fontSize: 13, color: isDark ? "#f0f0f0" : "#1e293b" }}>
                                        Observação Interna do Atendimento
                                    </Text>
                                </Space>
                                <Button
                                    size="small"
                                    type="link"
                                    icon={<EditOutlined />}
                                    onClick={() => setIsUpdateModalVisible(true)}
                                    style={{ padding: 0 }}
                                >
                                    {support?.observation ? "Editar" : "Adicionar"}
                                </Button>
                            </div>

                            {support?.observation ? (
                                <Paragraph
                                    style={{
                                        margin: 0,
                                        color: isDark ? "#d9d9d9" : "#334155",
                                        fontSize: 13,
                                        lineHeight: 1.6,
                                        whiteSpace: "pre-wrap",
                                        background: isDark ? "#1f1f1f" : "#f1f5f9",
                                        padding: "10px 12px",
                                        borderRadius: 6,
                                        borderLeft: "3px solid #1677ff",
                                    }}
                                >
                                    {support.observation}
                                </Paragraph>
                            ) : (
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    Nenhuma observação interna registrada para este atendimento.
                                </Text>
                            )}
                        </div>
                    </div>
                </Col>
            </Row>

            <ModalUpdateSupport
                open={isUpdateModalVisible}
                support={support || null}
                onClose={() => setIsUpdateModalVisible(false)}
                onSave={handleSaveStatusAndObservation}
                isSaving={isSaving}
            />
        </div>
    );
}
